import Spinners from "loader/Spinners";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useParams } from "react-router-dom";
import { formatDecimalNumber } from "utils/commonFun";
import { barIds } from "utils/dropdownValue/selectBoxValue";

const AreaChartReport = ({ graph = [], filterDay, graphData, selectedDay, loader, customDate }) => {
  const [chartType, setChartType] = useState("area");

  // ----------find-chat-type------bar||area-------------
  useEffect(() => {
    if (selectedDay === "custom") {
      if (customDate.from && customDate.to) {
        setChartType("bar");
      }
      return;
    }
    if (filterDay?.id === "today" || filterDay?.id === "yesterday") {
      setChartType("area");
    } else if (barIds.includes(filterDay?.id)) {
      setChartType("bar");
    }
  }, [selectedDay, filterDay, customDate]);


  // -----------find-to-history-or-not-----------------
  const hasHistoryData = graph?.some(
    (point) => point.history && point.history.length > 0
  );
  // ---------flattenedHistory--data--------------
  const flattenedHistory = hasHistoryData
    ? graph.flatMap(
      (point) =>
        point.history?.map((h) => ({
          timestamp: h.timestamp,
          solar: h.solar,
          dg: h.dg,
          gridImport: h.gridImport,
          gridExport: h.gridExport,
        })) || []
    )
    : graph;
  // -----------date-time--------------
  const categories = hasHistoryData
    ? flattenedHistory.map((h) => moment(h.timestamp).format("HH:mm"))
    : graph?.map((point) => point.time);

  const allSeries = {
    solar: {
      name: "Solar Generation",
      data: flattenedHistory?.map((point) => point.solar),
    },
    dg: {
      name: "DG Consumption",
      data: flattenedHistory?.map((point) => point.dg),
    },
    gridImport: {
      name: "Grid Import",
      data: flattenedHistory?.map((point) => point.gridImport),
    },
    gridExport: {
      name: "Grid Export",
      data: flattenedHistory?.map((point) => point.gridExport),
    },
  };

  const validKeys = ["solar", "dg", "grid"];
  const selectedKey = validKeys.includes(graphData) ? graphData : null;

  let displayedSeries = [];
  let color = [];

  if (selectedKey === "grid") {
    displayedSeries = [allSeries.gridImport, allSeries.gridExport];
    color = ["#C8B4F2", "#FFA556"];
  } else if (selectedKey) {
    if (selectedKey === "solar") {
      color = ["#24C1EC"];
    } else if (selectedKey === "dg") {
      color = ["#F8D57E"];
    }
    displayedSeries = [allSeries[selectedKey]];
  } else {
    displayedSeries = Object.values(allSeries); // show all if no match
    color = ["#24C1EC", "#F8D57E", "#C8B4F2", "#FFA556"];
  }
  // ---------use-only-bar-chat----show-zoom-------------------------
  const detectCategoryType = (categories) => {
    if (!categories || categories.length === 0) return 'category';

    const sample = categories[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(sample)) {
      return 'datetime';
    }
    // Check if it's a month abbreviation
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (months.includes(sample)) {
      return 'category';
    }
    // Check if it's a weekday
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    if (weekdays.includes(sample)) {
      return 'category';
    }
    // Fallback
    return 'category';
  };

  const categoryType = detectCategoryType(categories);
  // ----------------graph----------------------
  const chartData = chartType === 'area' ? {
    series: displayedSeries,
    options: {
      chart: {
        type: chartType,
        height: 350,
        stacked: chartType === "bar",
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: true
        },
        animations: {
          // enabled: chartType === "bar" ? false : true,
          enabled: true,
          easing: "easeout",
          speed: 1000,
        },
      },

      states: {
        normal: { filter: { type: "none", value: 0 } },
        hover: { filter: { type: "lighten", value: 0.1 } },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: { type: "none", value: 0 }, // 🔥 Key fix
        },
      },

      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "solid",
        opacity: 0.2,
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        tickAmount: 7,
        labels: {
          rotate: -45,
          style: {
            colors: "#999",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        tickAmount: 7,
        labels: {
          style: {
            colors: "#999",
            fontSize: "12px",
          },
          formatter: (val) => Number(val).toFixed(1),
        },
      },
      tooltip: {
        x: { format: "HH:mm" },
        y: {
          formatter: (val) => `${Number(val).toFixed(1)} kW`,
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "center",
        markers: {
          radius: 50,
        },
        showForSingleSeries: true,
      },
      colors: color,
      grid: { show: false },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],

    },
  }
    // ------------bar-graph-----------------
    :
    {
      series: displayedSeries,
      options: {
        chart: {
          type: 'bar',
          height: 350,
          stacked: true,
          toolbar: {
            show: false
          },
          zoom: {
            enabled: true
          },
          animations: {
            enabled: false,
          },
        },
        responsive: [{
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom',
              offsetX: -10,
              offsetY: 0
            }
          }
        }],
        xaxis: {
          type: categoryType,
          categories: categories,
          labels: {
            rotate: -45,
            style: {
              fontSize: '12px',
              colors: '#999',
            },
          },
        },
        states: {
          normal: { filter: { type: "none", value: 0 } },
          hover: { filter: { type: "none", value: 0 } },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: { type: "none", value: 0 }, // 🔥 Key fix
          },
        },
        fill: {
          type: "solid",
          opacity: 1,
        },
        dataLabels: { enabled: false },
        yaxis: {
          tickAmount: 7,
          labels: {
            style: {
              colors: "#999",
              fontSize: "12px",
            },
            formatter: (val) => Number(val).toFixed(1),
          },
        },
        tooltip: {
          x: {
            format: categoryType === 'datetime' ? 'dd MMM' : undefined,
          }
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
          markers: {
            shape: "circle",
            radius: 12,
          },
          showForSingleSeries: true,
        },
        colors: color,
        grid: { show: false },
      },
    }
  return (
    <div>
      {/* <h6 style={{ color: "#333" }}>{customDate.from && customDate.to? `${customDate.from}/${customDate.to}`:filterDay?.value}</h6> */}
      <h6 style={{ color: "#333" }}>{filterDay?.value}</h6>
      {loader ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "350px",
            width: "100%",
          }}
        >
          <Spinners />
        </div>
      ) : (
        <ReactApexChart
          key={chartType}
          options={chartData.options}
          series={chartData.series}
          type={chartType}
          height={350}
        />
      )}
    </div>
  );
};

export default AreaChartReport;